import { useReducer } from "react"
import { AddedFleetEntry, ResolvedFleetEntry } from "../../api/fleet"
import { usePolicyAndFleet } from "../../contexts"
import { HAVEN_ID } from "../../const/policies"
import { AddEntriesToFleet } from "./api/AddEntriesToFleet"
import { FindResolvedEntries } from "./api/FindResolvedEntries"
import { ConfirmAddFleetEntries } from "./components/ConfirmAddFleetEntries"
import { FormAndCSVUpload } from "./components/FormAndCSVUpload"
import { ManuallyResolveEntries } from "./components/resolveEntries/ManuallyResolveEntries"
import { AddToFleetState, addToFleetInitialState, addToFleetReducer } from "./reducers/addToFleetReducer"

export const ADD_TO_FLEET_CONTENT_DTI = {
  FORM_AND_CSV: `add_to_fleet_form_and_csv`,
  MANUALLY_RESOLVE_ENTRIES: `add_to_fleet_manually_resolve`,
  CONFIRM: `add_to_fleet_confirm`,
}

// Passing initial state is enabled in order to facilitate testing
export type AddToFleetContentProps = {
  initialState?: AddToFleetState
}

export const AddToFleetContent = ({ initialState }: AddToFleetContentProps) => {
  const { policy } = usePolicyAndFleet()
  const isHavenPolicy = policy.underwriter.id === HAVEN_ID
  const [state, dispatch] = useReducer(addToFleetReducer, initialState || addToFleetInitialState)
  const { validFleetEntries, unresolvedFleetEntries, resolvedFleetEntries, manuallyResolvedFleetEntries, status } =
    state
  if (status === `validated`)
    return (
      <FindResolvedEntries
        entries={validFleetEntries}
        onDone={(
          resolved: ReadonlyArray<ResolvedFleetEntry>,
          unresolved: ReadonlyArray<AddedFleetEntry>,
          vehicleSetId: string
        ) => dispatch({ type: `setResolvedAndUnresolvedFleetEntries`, resolved, unresolved, vehicleSetId })}
      />
    )
  if (status === `serverResolved`)
    return unresolvedFleetEntries.length ? (
      <ManuallyResolveEntries
        dataTestId={ADD_TO_FLEET_CONTENT_DTI.MANUALLY_RESOLVE_ENTRIES}
        entries={unresolvedFleetEntries}
        onDone={(entries: ReadonlyArray<ResolvedFleetEntry>) => dispatch({ type: `entriesManuallyResolved`, entries })}
        onCancel={() => dispatch({ type: `cancel` })}
      />
    ) : (
      <ConfirmAddFleetEntries
        dataTestId={ADD_TO_FLEET_CONTENT_DTI.CONFIRM}
        entries={resolvedFleetEntries}
        onBack={() => dispatch({ type: `cancel` })}
        onConfirmed={() => dispatch({ type: `confirm` })}
      />
    )
  if (status === `confirmed`)
    return (
      <AddEntriesToFleet
        entries={resolvedFleetEntries}
        manualEntries={manuallyResolvedFleetEntries}
        vehicleSetId={state.vehicleSetId}
      />
    )
  return (
    <FormAndCSVUpload
      dataTestId={ADD_TO_FLEET_CONTENT_DTI.FORM_AND_CSV}
      addedEntryFormRows={validFleetEntries}
      enableBulkUpload={!isHavenPolicy}
      onValidEntriesAdded={(entries: ReadonlyArray<AddedFleetEntry>) =>
        dispatch({ type: `setValidFleetEntries`, entries })
      }
    />
  )
}

AddToFleetContent.defaultProps = {
  initialState: addToFleetInitialState,
}
